/**
 * ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9): the case management note's own content, on the live
 * (Progress Note V141) note engine: its phrase pills, its rough-notes hint, the rail cards and
 * Quick add switches it has no use for, its plan goals setting and its Default billing code
 * choices. Pure, so it's tested on its own.
 */
import { SERVICE_CODE_CHOICES, goalsSettingKind, type PracticeNoteSettings } from '@ehr/shared';
import { cptLabel } from './noteBilling.js';
import { SERVICE_CODES } from './noteV81.js';

export const CASE_MANAGEMENT = 'Case management';

/**
 * V9 DRAWER_PHRASES (Case Management Note V2): What happened and Next step offer case management
 * wording, never the therapy phrases (anxiety ratings, sessions).
 */
export const CASE_MANAGEMENT_PHRASES: Readonly<Record<string, readonly string[]>> = {
  subjective: [
    'Met with client to __.',
    'Helped client complete __.',
    "Contacted __ on the client's behalf.",
    'Linked client to __.',
    'Client reported __ since the last contact.',
  ],
  plan: [
    'Next contact is scheduled for __.',
    'Follow up with __ by __.',
    'Client will __ before the next contact.',
  ],
};

/** The rail switches of Note settings > Quick add: Show Quick add, then the side cards. */
export type RailSwitch = 'quick' | 'measures' | 'last' | 'changes' | 'gen';

/**
 * V9 (16 A): no Quick add, Measures or My changes card on a case management note, and no switch
 * for them anywhere (Show Last session and Show Generate Notes stay). Other kinds: none off.
 */
export function railOffFor(kind: string): readonly RailSwitch[] {
  return kind === CASE_MANAGEMENT ? ['quick', 'measures', 'changes'] : [];
}

/**
 * V9 (18 A): the smaller Quick add tab on a case management note has the tabs whose items Add to
 * note (Ratings, Checklists, Boxes); no Dot phrases tab.
 */
export const quickDotTabOff = (kind: string): boolean => kind === CASE_MANAGEMENT;

/**
 * V9 (20 A to 22 A): Treatment plan goals and Problems, goals, and objectives addressed show on a
 * case management note only while Practice note settings > Show treatment plan goals is on.
 */
export const planGoalsOn = (
  kind: string,
  practice: Pick<PracticeNoteSettings, 'caseManagementGoals'>,
): boolean => goalsSettingKind(kind) && practice.caseManagementGoals;

/** V9 V7: the rough notes hint names What happened and Next step, never interventions. */
export const CASE_ROUGH_PLACEHOLDER =
  'Type, paste or dictate your rough notes from the contact. Generate Notes fills What happened and Next step from them; review each box before signing.';

/**
 * V9 (14 A): the Default billing code a case management note starts with: the practice's (''
 * is No default, billed by hand), else the built-in H0006.
 */
export function defaultCodeOf(kind: string, codes: Partial<Record<string, string>>): string {
  const set = codes[kind];
  return set !== undefined ? set : (SERVICE_CODES[kind] ?? '');
}

/** V9 (14 A): the Default billing code dropdown: No default, then each code with its name. */
export function defaultCodeOptions(): { value: string; label: string }[] {
  return [
    { value: '', label: 'No default' },
    ...SERVICE_CODE_CHOICES.map((c) => ({ value: c, label: cptLabel(c) })),
  ];
}
